import assert from 'node:assert/strict'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import {
  DocumentRenderer,
  type DocumentRendererProps,
} from '@keystone-6/document-renderer'
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
  SerializeFrom,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import clsx from 'clsx'
import groupBy from 'just-group-by'
import React from 'react'
import { PromoBar } from '~/components/PromoBar'
import { revalidateFlight } from '~/db/keystone.server'
import AFFLogo from '~/images/logos/australian-frequent-flyer'
import PointHacksLogo from '~/images/logos/point-hacks'
import { fetchAllStaticContent } from '~/models/content.server'
import { fetchReportById } from '~/models/report.v1.server'
import { usePageView } from '~/utils/gtag'

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    {
      title: `${data?.report.brand} Concierge Report for ${data?.customerFirstName}`,
    },
    { name: 'robots', content: 'noindex' },
  ]
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const intent = formData.get('intent')

  if (intent === 'validateFlight') {
    const id = formData.get('id') as string
    const flightNumber = formData.get('flightNumber') as string
    const err = await revalidateFlight({ id, flightNumber })
    if (err && err.length > 0) {
      console.error(err)
      throw new Response(JSON.stringify(err), { status: 500 })
    }
  }
  return new Response(JSON.stringify({}), { status: 201 })
}

export async function loader({ params }: LoaderFunctionArgs) {
  assert.ok(params.id, 'Must hava a ID param for this route to load')
  const [reportResult, contentResult] = await Promise.allSettled([
    fetchReportById(params.id),
    fetchAllStaticContent(),
  ])

  if (reportResult.status === 'rejected') {
    throw new Response('Error fetching report', { status: 500 })
  }

  if (contentResult.status === 'rejected') {
    throw new Response('Error fetching report', { status: 500 })
  }

  const customerFirstName = reportResult.value.quote?.customerFirstName

  const howToBookContent = reportResult.value.redemptionOptions.flatMap((o) =>
    Object.values(groupBy(o.howToBook, (o) => o.id as string)).map((o) => o[0]),
  )

  const cabinContent = reportResult.value.redemptionOptions.map((o) => ({
    cabinComments: o.cabinComments,
    cabinExperiences: Object.values(
      groupBy(o.cabinExperience, (o) => o.id as string),
    ).map((o) => o[0]),
  }))
  const hasCabinContent = cabinContent.some(
    (c) => c.cabinExperiences.length || !documentIsEmpty(c.cabinComments),
  )

  const loungeContent = reportResult.value.redemptionOptions.map((o) => ({
    loungeComments: o.loungeComments,
    loungeExperiences: Object.values(
      groupBy(o.loungeExperience, (o) => o.id as string),
    ).map((o) => o[0]),
  }))
  const hasLoungeContent = loungeContent.some(
    (c) => c.loungeExperiences.length || !documentIsEmpty(c.loungeComments),
  )

  const airportContent = reportResult.value.redemptionOptions.map((o) => ({
    airportComments: o.airportComments,
    airportExperiences: Object.values(
      groupBy(o.airportExperience, (o) => o.id as string),
    ).map((o) => o[0]),
  }))
  const hasAirportContent = airportContent.some(
    (c) => c.airportExperiences.length || !documentIsEmpty(c.airportComments),
  )

  const promoBarText = contentResult.value.find(
    (c) => c.id === 'clxles3yg000bvdn1jfv2zgyh',
  )?.shortText

  const heroImage = contentResult.value
    .find((c) => c.id === '20e1ae37-94e7-4eff-ab68-ebca05741f67')
    ?.images.at(0)

  const flightsImage = contentResult.value
    .find((c) => c.id === '35438501-93f5-4d4a-b026-7d0c6ac7fa2d')
    ?.images.at(0)

  const howToBookImage = contentResult.value
    .find((c) => c.id === '70599791-58dd-4b7c-8353-c1f80ee01b16')
    ?.images.at(0)

  const openingDocument = contentResult.value.find(
    (c) => c.id === '3b7fa32a-b17f-4962-8ed2-76ae4d8aaad0',
  )?.longText

  const transferInstructionsDocument = contentResult.value.find(
    (c) => c.id === '00ddef47-ff7a-468c-a163-b26e15227cc5',
  )?.longText

  const travelRequirementsDocument = contentResult.value.find(
    (c) => c.id === '100849f9-639d-4aaf-ba71-05232b703fbd',
  )?.longText

  const disclaimerDocument = contentResult.value.find(
    (c) => c.id === 'e7407a98-4154-435d-80fc-67c8e279722d',
  )?.longText

  const footerDocument = contentResult.value.find(
    (c) => c.id === '8bb5584b-6c79-4bd7-87c3-0db4b6a06b60',
  )?.longText

  const footerImage = contentResult.value
    .find((c) => c.id === '680e06ff-fc71-484e-a837-711cede422d8')
    ?.images.at(0)

  return {
    customerFirstName,
    report: reportResult.value,
    promoBarText,
    heroImage,
    openingDocument,
    flightsImage,
    howToBookImage,
    howToBookContent,
    transferInstructionsDocument,
    cabinContent,
    hasCabinContent,
    loungeContent,
    hasLoungeContent,
    airportContent,
    hasAirportContent,
    travelRequirementsDocument,
    disclaimerDocument,
    footerDocument,
    footerImage,
  }
}

type RedemptionOption = SerializeFrom<
  typeof loader
>['report']['redemptionOptions'][number]
export default function Report() {
  const {
    customerFirstName,
    report,
    heroImage,
    flightsImage,
    howToBookImage,
    howToBookContent,
    openingDocument,
    cabinContent,
    hasCabinContent,
    loungeContent,
    hasLoungeContent,
    airportContent,
    hasAirportContent,
    transferInstructionsDocument,
    travelRequirementsDocument,
    disclaimerDocument,
    footerDocument,
    footerImage,
  } = useLoaderData<typeof loader>()

  usePageView()

  return (
    <div
      className={clsx(
        report.isPublicPromo
          ? null
          : report.brand === 'Point Hacks'
            ? 'border-[#c00] border-t-8'
            : 'border-[#283071] border-t-8',
      )}
    >
      <PromoBar />
      {report.brand === 'Point Hacks' ? <PointHacksLogo /> : null}
      {report.brand === 'Frequent Flyer' ? <AFFLogo /> : null}
      {heroImage ? (
        <img
          width={heroImage.upload_width?.toString()}
          height={heroImage.upload_height?.toString()}
          className="mt-6 w-full max-h-[50vw] lg:max-h-[33vw] object-cover"
          src={`https://concierge-cms.syd1.cdn.digitaloceanspaces.com/${heroImage.upload_id}.${heroImage.upload_extension}`}
          alt={heroImage.caption ?? undefined}
        />
      ) : null}

      <main className="container max-w-5xl p-8 mx-auto prose prose-zinc prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg">
        <h1 className="sr-only">Concierge Report</h1>
        <div className="flex flex-wrap items-center justify-between mt-3 mb-6">
          <h2 className="!m-0">Your Search Results</h2>
          {report.approvedAt ? (
            <p className="!m-0">
              Prepared for {customerFirstName} on{' '}
              {Intl.DateTimeFormat().format(new Date(report.approvedAt))}
            </p>
          ) : null}
        </div>
        <p>Hi {customerFirstName},</p>
        <DocumentRenderer
          document={openingDocument as DocumentRendererProps['document']}
        />
        <h3>Report Contents</h3>
        <ul className="mb-12 list-none">
          <li>
            <a href="#flights">Flights</a>
          </li>
          <li>
            <a href="#how-to-book">How to Book</a>
          </li>
          {hasCabinContent ? (
            <li>
              <a href="#cabin-experience">Cabin Experience</a>
            </li>
          ) : null}
          {hasLoungeContent ? (
            <li>
              <a href="#lounge-experience">Lounge Experience</a>
            </li>
          ) : null}
          {hasAirportContent ? (
            <li>
              <a href="#airport-tips">Airport Tips</a>
            </li>
          ) : null}
        </ul>

        <hr />
        <h2 id="flights">Recommended Award Flights</h2>
        {report.redemptionOptions.map((option) => (
          <section key={option.id}>
            <h4>Pricing and Points Details</h4>
            <ul>
              <li>
                <span className="mr-1 font-bold">Total Points Required</span>:{' '}
                {Intl.NumberFormat('en-AU').format(
                  (option.passengers ?? 1) * option.pointsPrice +
                    (option.infants ?? 0) * (option.infantPointsPrice ?? 0),
                )}{' '}
                {option.pointsCurrency}
              </li>
              <li>
                <span className="mr-1 font-bold">Total Taxes</span>:{' '}
                {Intl.NumberFormat('en-AU', {
                  currency: option.taxesCurrency,
                  style: 'currency',
                }).format(
                  (option.passengers ?? 1) * option.taxesPrice +
                    (option.infants ?? 0) * (option.infantTaxesPrice ?? 0),
                )}{' '}
                ({option.taxesCurrency})
              </li>
              <li>
                <span className="mr-1 font-bold">Passengers</span>:{' '}
                {option.passengers}
              </li>
              {option.infants ? (
                <li>
                  <span className="mr-1 font-bold">Infants</span>:{' '}
                  {option.infants}
                </li>
              ) : null}
            </ul>

            <Flights flights={option.flights} />

            <DocumentRenderer
              document={option.notes as DocumentRendererProps['document']}
            />

            {flightsImage ? (
              <img
                width={flightsImage.upload_width?.toString()}
                height={flightsImage.upload_height?.toString()}
                className="w-full mt-6"
                src={`https://concierge-cms.syd1.cdn.digitaloceanspaces.com/${flightsImage.upload_id}.${flightsImage.upload_extension}`}
                alt={flightsImage.caption ?? undefined}
              />
            ) : null}

            {documentIsEmpty(option.bookingComments) ? null : (
              <h3>Concierge Booking Comments</h3>
            )}
            <DocumentRenderer
              document={
                option.bookingComments as DocumentRendererProps['document']
              }
            />
          </section>
        ))}

        {report.transferInstructions.length ? (
          <section
            id="transfer-instructions"
            className="p-6 rounded-sm bg-zinc-50"
          >
            <Disclosure>
              <DisclosureButton className="w-full text-left">
                {({ open }) => (
                  <div>
                    <div className="flex justify-between">
                      <h2 className="m-4">Transfer Instructions</h2>
                      {open ? (
                        <ChevronUpIcon className="block w-8 ml-4" />
                      ) : (
                        <ChevronDownIcon className="block w-8 ml-4" />
                      )}
                    </div>
                    <DocumentRenderer
                      document={
                        transferInstructionsDocument as DocumentRendererProps['document']
                      }
                    />
                  </div>
                )}
              </DisclosureButton>
              <DisclosurePanel>
                <h4>Step-by-Step Instructions</h4>
                {report.transferInstructions.map((content) => (
                  <div
                    key={content.id}
                    className="prose-sm prose-h2:text-xl prose-h3:text-lg prose-h4:text-base"
                  >
                    <h4>{content.title}</h4>
                    {content.externalLink ? (
                      <a href={content.externalLink}>{content.externalLink}</a>
                    ) : null}
                    <DocumentRenderer
                      document={
                        content.content as DocumentRendererProps['document']
                      }
                    />
                    <ImageList images={content.images} />
                  </div>
                ))}
              </DisclosurePanel>
            </Disclosure>
          </section>
        ) : null}

        <hr />
        <section id="how-to-book" className="p-6 rounded-sm bg-zinc-50">
          <Disclosure>
            <DisclosureButton className="w-full text-left">
              {({ open }) => (
                <div>
                  <div className="flex justify-between">
                    <h2 className="m-4">How to Book Instructions</h2>
                    {open ? (
                      <ChevronUpIcon className="block w-8 ml-4" />
                    ) : (
                      <ChevronDownIcon className="block w-8 ml-4" />
                    )}
                  </div>
                  <ol>
                    {howToBookContent.map((content) => (
                      <li key={content.id}>
                        <a href={`#${content.id}`}>{content.title}</a>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </DisclosureButton>
            <DisclosurePanel>
              <h4>Step-by-Step Instructions</h4>
              {howToBookContent.map((content) => (
                <div key={content.id} id={content.id as string}>
                  <h3>{content.title}</h3>
                  {content.externalLink ? (
                    <a href={content.externalLink}>{content.externalLink}</a>
                  ) : null}
                  <DocumentRenderer
                    document={
                      content.content as DocumentRendererProps['document']
                    }
                  />
                  <ImageList images={content.images} />
                </div>
              ))}
              {report.redemptionOptions.map((r) => (
                <ImageList key={r.id} images={r.screenshots} />
              ))}
            </DisclosurePanel>
          </Disclosure>
        </section>

        {howToBookImage ? (
          <img
            width={howToBookImage.upload_width?.toString()}
            height={howToBookImage.upload_height?.toString()}
            className="w-full mt-6"
            src={`https://concierge-cms.syd1.cdn.digitaloceanspaces.com/${howToBookImage.upload_id}.${howToBookImage.upload_extension}`}
            alt={howToBookImage.caption ?? undefined}
          />
        ) : null}

        <hr />
        <section id="cabin-experience">
          {hasCabinContent || hasLoungeContent || hasAirportContent ? (
            <h2>Your Cabin, Lounge and Airport Experience</h2>
          ) : null}

          {hasCabinContent ? <h3>Cabin Experience</h3> : null}

          {cabinContent.map(({ cabinComments }, i) => (
            <DocumentRenderer
              key={i}
              document={cabinComments as DocumentRendererProps['document']}
            />
          ))}
          {cabinContent.map(({ cabinExperiences }, i) => (
            <div key={i}>
              {cabinExperiences.map((content) => (
                <div key={content.id} className="mb-6 prose-sm">
                  <h4>{content.title}</h4>
                  {content.externalLink ? (
                    <a href={content.externalLink}>{content.externalLink}</a>
                  ) : null}
                  <DocumentRenderer
                    document={
                      content.content as DocumentRendererProps['document']
                    }
                  />
                  <ImageList images={content.images} />
                </div>
              ))}
            </div>
          ))}

          {hasLoungeContent ? (
            <h3 id="lounge-experience">Lounge Experience</h3>
          ) : null}

          {loungeContent.map(({ loungeComments }, i) => (
            <DocumentRenderer
              key={i}
              document={loungeComments as DocumentRendererProps['document']}
            />
          ))}
          {loungeContent.map(({ loungeExperiences }, i) => (
            <div key={i}>
              {loungeExperiences.map((content) => (
                <div key={content.id} className="mb-6 prose-sm">
                  <h4>{content.title}</h4>
                  {content.externalLink ? (
                    <a href={content.externalLink}>{content.externalLink}</a>
                  ) : null}
                  <DocumentRenderer
                    document={
                      content.content as DocumentRendererProps['document']
                    }
                  />
                  <ImageList images={content.images} />
                </div>
              ))}
            </div>
          ))}

          {hasAirportContent ? (
            <h3 id="airport-tips">Airport Experience</h3>
          ) : null}
          {airportContent.map(({ airportComments }, i) => (
            <DocumentRenderer
              key={i}
              document={airportComments as DocumentRendererProps['document']}
            />
          ))}
          {airportContent.map(({ airportExperiences }, i) => (
            <div key={i}>
              {airportExperiences.map((content) => (
                <div key={content.id} className="mb-6 prose-sm">
                  <h4>{content.title}</h4>
                  {content.externalLink ? (
                    <a href={content.externalLink}>{content.externalLink}</a>
                  ) : null}
                  <DocumentRenderer
                    document={
                      content.content as DocumentRendererProps['document']
                    }
                  />
                  <ImageList images={content.images} />
                </div>
              ))}
            </div>
          ))}
        </section>

        <section>
          <h3>International Travel Requirements</h3>
          <DocumentRenderer
            document={
              travelRequirementsDocument as DocumentRendererProps['document']
            }
          />
        </section>

        <hr />
        <section>
          <h2>Disclaimer</h2>
          <DocumentRenderer
            document={disclaimerDocument as DocumentRendererProps['document']}
          />
        </section>

        <section>
          <DocumentRenderer
            document={footerDocument as DocumentRendererProps['document']}
          />
        </section>
      </main>
      {footerImage ? (
        <img
          width={footerImage.upload_width?.toString()}
          height={footerImage.upload_height?.toString()}
          className="mt-6 w-full max-h-[50vw] lg:max-h-[33vw] object-cover"
          src={`https://concierge-cms.syd1.cdn.digitaloceanspaces.com/${footerImage.upload_id}.${footerImage.upload_extension}`}
          alt={footerImage.caption ?? undefined}
        />
      ) : null}
    </div>
  )
}

function Flights({ flights }: { flights: RedemptionOption['flights'] }) {
  const sortedFlights = flights.sort(
    (a, b) =>
      new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime(),
  )

  return (
    <div className="flow-root mt-8 not-prose">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full divide-y divide-zinc-300">
            <thead className="sr-only">
              <tr>
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-zinc-900 sm:pl-0"
                >
                  Airline
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-zinc-900"
                >
                  Flight Number
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-zinc-900"
                >
                  Departure
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-zinc-900"
                >
                  Arrival
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-zinc-900"
                >
                  Duration
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-zinc-900"
                >
                  Class
                </th>
                <th
                  scope="col"
                  className="px-3 py-3.5 text-left text-sm font-semibold text-zinc-900"
                >
                  Aircraft
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {sortedFlights.map((f, i) =>
                f.amadeus ? (
                  <React.Fragment key={f.flightNumber}>
                    <tr className="text-base font-bold">
                      <td>Flight {i + 1}</td>
                      <td colSpan={6}>
                        {processFlightPath(f.amadeus)} on{' '}
                        {Intl.DateTimeFormat('en-AU', {
                          dateStyle: 'full',
                        }).format(new Date(f.departureDate))}
                      </td>
                    </tr>

                    <tr>
                      <td className="py-5 pl-4 pr-3 text-sm whitespace-nowrap sm:pl-0">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10">
                            <img
                              className="w-10 h-10"
                              src={`/airlines/tail/${f.flightNumber.slice(
                                0,
                                2,
                              )}.svg`}
                              alt=""
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-5 text-sm whitespace-nowrap text-zinc-900">
                        {f.flightNumber}
                      </td>
                      <td className="px-3 py-5 text-sm whitespace-nowrap text-zinc-900">
                        {f.amadeus?.flightPoints.at(0)?.departure?.timings[0]
                          .value
                          ? processTimeAtTimezone(
                              f.amadeus?.flightPoints.at(0)?.departure
                                ?.timings[0].value,
                            )
                          : null}
                      </td>
                      <td className="px-3 py-5 text-sm whitespace-nowrap text-zinc-900">
                        {f.amadeus?.flightPoints.at(-1)?.arrival?.timings[0]
                          .value
                          ? processTimeAtTimezone(
                              f.amadeus?.flightPoints.at(-1)?.arrival
                                ?.timings[0].value,
                            )
                          : null}
                      </td>
                      <td className="px-3 py-5 text-sm whitespace-nowrap text-zinc-900">
                        {durationToString(
                          f.amadeus?.segments.find(
                            (s) =>
                              s.boardPointIataCode === f.route.slice(0, 3) &&
                              s.offPointIataCode === f.route.slice(-3),
                          )?.scheduledSegmentDuration,
                        )}
                      </td>
                      <td className="px-3 py-5 text-sm whitespace-nowrap text-zinc-900">
                        {f.class}
                      </td>
                      <td className="text-xs no-prose">
                        {f.amadeus.legs.map((leg, i, arr) => (
                          <div key={i}>
                            <p>
                              {leg.boardPointIataCode} - {leg.offPointIataCode}
                            </p>
                            <p>{durationToString(leg.scheduledLegDuration)}</p>
                            {i === 0 && arr.length === 2 && f.amadeus ? (
                              <p className="text-gray-500">
                                Stopover: {processStopoverDuration(f.amadeus)}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </td>
                    </tr>
                    {i < sortedFlights.length - 1 &&
                    processLayoverDuration(sortedFlights, f) ? (
                      <tr className="!border-t-0">
                        <td colSpan={7}>
                          Connection: {processLayoverDuration(sortedFlights, f)}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ) : (
                  <tr className="text-base font-bold" key={f.flightNumber}>
                    <td className="justify-between w-full py-6" colSpan={7}>
                      <form method="post">
                        <input
                          type="hidden"
                          name="intent"
                          value="validateFlight"
                        />
                        <input type="hidden" name="id" value={f.id} />
                        <input
                          type="hidden"
                          name="flightNumber"
                          value={f.flightNumber}
                        />
                        <button type="submit" className="p-1 bg-yellow-100">
                          Error validating {f.flightNumber} - Revalidate?
                        </button>
                      </form>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function ImageList({
  images,
}: {
  images: {
    id: string | null
    order: number | null
    caption: string | null
    upload_id: string | null
    upload_extension: string | null
    description?: unknown
  }[]
}) {
  return images
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((img) => (
      <figure key={img.id}>
        <img
          className="mx-auto block max-h-[500px] border border-solid border-zinc-300"
          src={`https://concierge-cms.syd1.cdn.digitaloceanspaces.com/${img.upload_id}.${img.upload_extension}`}
          alt={img.caption ?? undefined}
        />
        {img.caption ? (
          <figcaption className="block mt-0 text-sm text-center">
            {img.caption}
          </figcaption>
        ) : null}
        {img.description ? (
          <div className="my-6">
            <DocumentRenderer
              document={img.description as DocumentRendererProps['document']}
            />
          </div>
        ) : null}
      </figure>
    ))
}

function documentIsEmpty(docs: unknown) {
  return (docs as DocumentRendererProps['document']).every((doc) =>
    doc.children.every((child) => child.text === ''),
  )
}

function durationToString(duration?: string): string {
  const d = processDuration(duration)
  if (!d) return ''
  return `${d.hours}h ${d.minutes}m`
}

/**
 * @param duration e.g PT7H40M
 */
function processDuration(
  duration?: string,
): Record<'hours' | 'minutes', number> {
  if (!duration) {
    return { hours: 0, minutes: 0 }
  }
  const cleanDuration = duration.replace('PT', '').replace('M', '')
  const [hours, minutes] = cleanDuration.includes('H')
    ? cleanDuration.split('H')
    : [0, cleanDuration]

  return { hours: Number(hours), minutes: Number(minutes) }
}

function processLayoverTimeDifferenceInMinutes(
  landingTimings?: { qualifier: string; value: string }[],
  takeoffTimings?: { qualifier: string; value: string }[],
) {
  const landingStr = landingTimings?.[0].value.split('+')[0]
  const takeoffStr = takeoffTimings?.[0].value.split('+')[0]
  if (!landingStr || !takeoffStr) return null
  const landingEpoch = new Date(landingStr).getTime()
  const takeoffEpoch = new Date(takeoffStr).getTime()

  return Math.ceil(Math.abs(takeoffEpoch - landingEpoch) / (1000 * 60))
}

function processSegments(schedule: FlightSchedule): {
  origin: FlightPoint
  destination: FlightPoint
  journey: Segment | null
  firstLeg: Segment
  lastLeg: Segment
  stopover: string | null
} | null {
  const [origin, destination, ...rest] = schedule.flightPoints
  if (rest && rest.length > 0) {
    return null
  }

  // Filter out the segment that represents the entire journey
  const segments =
    schedule.segments.length === 1
      ? schedule.segments
      : schedule.segments.filter(
          (s) =>
            !(
              s.boardPointIataCode === origin.iataCode &&
              s.offPointIataCode === destination.iataCode
            ),
        )

  const journey =
    schedule.segments.find(
      (s) =>
        s.boardPointIataCode === origin.iataCode &&
        s.offPointIataCode === destination.iataCode,
    ) ?? null

  const firstLeg = segments.find(
    (s) => s.boardPointIataCode === origin.iataCode,
  )
  const lastLeg = segments.find(
    (s) => s.offPointIataCode === destination.iataCode,
  )
  if (!firstLeg || !lastLeg) {
    return null
  }

  const stopover = segments.length > 1 ? firstLeg.offPointIataCode : null
  return {
    origin,
    destination,
    journey,
    firstLeg,
    lastLeg,
    stopover,
  }
}

function processStopoverDuration(schedule: FlightSchedule): string | null {
  const segments = processSegments(schedule)
  if (!segments || !segments.journey) {
    return '~ Error ~'
  }
  const journeyDuration = processDuration(
    segments.journey.scheduledSegmentDuration,
  )
  const firstLegDuration = processDuration(
    segments.firstLeg.scheduledSegmentDuration,
  )
  const lastLegDuration = processDuration(
    segments.lastLeg.scheduledSegmentDuration,
  )

  const stopoverMinutes =
    journeyDuration.hours * 60 +
    journeyDuration.minutes -
    (firstLegDuration.hours * 60 + firstLegDuration.minutes) -
    (lastLegDuration.hours * 60 + lastLegDuration.minutes)

  // There is no stopover
  if (stopoverMinutes < 0) return null

  // Stopover is longer than a day
  if (stopoverMinutes >= 1440) return null

  const hours = Math.floor(stopoverMinutes / 60) // Get whole hours
  const minutes = stopoverMinutes % 60 // Get remaining minutes

  return `${hours}h ${minutes}m`
}

function processLayoverDuration(
  flights: RedemptionOption['flights'],
  currentFlight: RedemptionOption['flights'][number],
): string | null {
  if (!currentFlight.amadeus) return '~ Error ~'
  const currentIndex = flights.findIndex((f) => f.id === currentFlight.id)
  const nextFlight = flights[currentIndex + 1]
  if (!nextFlight || !nextFlight.amadeus) return '~ Error ~'

  const currentFlightSegments = processSegments(currentFlight.amadeus)
  if (!currentFlightSegments || !currentFlightSegments.journey) {
    return '~ Error ~'
  }

  const nextFlightSegments = processSegments(nextFlight.amadeus)
  if (!nextFlightSegments || !nextFlightSegments.journey) {
    return '~ Error ~'
  }
  const layoverMinutes = processLayoverTimeDifferenceInMinutes(
    currentFlightSegments.destination.arrival?.timings,
    nextFlightSegments.origin.departure?.timings,
  )

  if (layoverMinutes === null) return null

  // Something went wrong
  if (layoverMinutes < 0) return null
  // Stopover is longer than a day
  if (layoverMinutes >= 1440) return null
  const hours = Math.floor(layoverMinutes / 60) // Get whole hours
  const minutes = layoverMinutes % 60 // Get remaining minutes
  return `${hours}h ${minutes}m`
}

function processFlightPath(schedule: FlightSchedule): string {
  const segments = processSegments(schedule)
  if (!segments) {
    return '~ Error ~'
  }
  const { origin, destination, stopover } = segments
  return stopover
    ? `${origin.iataCode} to ${destination.iataCode}, via ${stopover}`
    : `${origin.iataCode} to ${destination.iataCode}`
}

function processTimeAtTimezone(dateTime?: string): string | null {
  if (!dateTime) return null
  return dateTime.substring(11, 16)
}
